<template>
  <div class="relative">
    <div class="flex items-center">
      <h2 class="w-full">{{ t('additionalDrivers') }}</h2>
      <CVButton
        v-if="!preventAdding && !isCustomBookedExtra"
        type="button"
        class="min-w-max"
        :disabled="disabled"
        @click="push(new AdditionalDriver())"
      >
        {{ t('add') }}
      </CVButton>
    </div>
    <div v-if="fields.length" class="col-span-1 mt-4 md:col-span-2">
      <div class="grid grid-cols-1 gap-4">
        <div
          v-for="(field, idx) in fields"
          :key="field.key"
          class="relative grid gap-x-4 gap-y-2 rounded-md bg-white p-4 md:grid-cols-3"
        >
          <InputField
            :name="`additionalDrivers[${idx}].firstName`"
            :initial-value="field.value.firstName"
            :label="t('firstName')"
            :disabled="disabled"
          />
          <InputField
            :name="`additionalDrivers[${idx}].lastName`"
            :initial-value="field.value.lastName"
            :label="t('lastName')"
            :disabled="disabled"
          />
          <DatepickerField
            :label="t('dateOfBirth')"
            :initial-value="field.value.dateOfBirth"
            :name="`additionalDrivers[${idx}].dateOfBirth`"
            :disabled="disabled"
            calendar-date
          />
          <div class="absolute right-2 top-2">
            <button
              v-if="!isCustomBookedExtra"
              data-testid="remove-additional-driver"
              class="h-7 w-7 text-xl font-bold text-red-500"
              type="button"
              :disabled="disabled"
              @click="remove(idx)"
            >
              <XMarkIcon />
            </button>
          </div>
          <CVButton
            v-if="isDocumentScanningEnabled && props.showLicenseScan"
            type="button"
            class="min-w-max"
            :disabled="disabled"
            @click="emit('scan-documents', idx + 1)"
          >
            <CameraIcon class="h-4 w-4" />
            {{ t('scanLicense') }}
          </CVButton>
        </div>
      </div>
    </div>
    <div v-if="hasChanged && !fields.length" class="mt-4 rounded-md bg-white p-2 pb-1 pt-1.5">
      <p class="text-red-500">
        {{ t('deleteAllAdditionalDrivers') }}
      </p>
    </div>
    <div v-if="hasChanged" class="mt-4 flex items-center justify-end gap-4">
      <p v-if="hasPriceChanged && !isCalculatingPrice" class="font-medium">
        {{ t('newPrice') }}:
        <span v-currency="newPricing?.totalPrice"></span>
      </p>
      <p v-if="isCalculatingPrice" class="mb-2 font-medium">
        {{ t('recalculatingPrice') }}
      </p>
      <CVButton
        variant="default"
        :is-loading="isSaving"
        :disabled="isCalculatingPrice"
        @click.prevent="onSubmit"
        >{{ t('save') }}</CVButton
      >
      <CVButton :disabled="isSaving" @click.prevent="onCancel">{{ t('cancel') }}</CVButton>
    </div>
    <div v-if="isSaving" class="absolute -inset-3 flex items-center justify-center bg-black/20">
      <Spinner />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getBookedExtras, getCustomBookedExtras } from '@/utils/pricing.utils';
import DatepickerField from '@/components/DatepickerField.vue';
import InputField from '@/components/InputField.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { AdditionalDriver } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash';
import { useFieldArray, useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import type { ChangedBookedExtraTypes } from '@/components/price-change-confirmation/price-change-confirmation';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';
import {
  additionalDriversMandatorySchema,
  additionalDriversSchema,
} from '@/validation/booking.schema';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { CameraIcon } from 'lucide-vue-next';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useUpdateBooking } from '@/queries/use-bookings.ts';
import {
  type BookedExtraDto,
  CounterpartyType,
  PriceRecalculationMode,
  type VehicleExtra,
  type VehicleExtraOption,
  VehicleExtraType,
} from '@carvia/ros-client-types';
import type { Pricing } from '@/entities/pricing/pricing.entity';
import { fromCalendarDate } from '@/utils/calendar-date';

const props = defineProps<{
  booking: Booking;
  disabled?: boolean;
  preventAdding?: boolean;
  validationIsMandatory?: boolean;
  showLicenseScan?: boolean;
  /** When set on agency-paid bookings, skips the counterparty picker (e.g. handover flow). */
  priceDifferenceCounterparty?: CounterpartyType;
  /** Handover wizard adjustments use the post-start-date cancellation fee tier. */
  handoverAdjustment?: boolean;
}>();
const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
  (e: 'scan-documents', value: number): void;
}>();

const { t } = useI18n();

const { isPending: isRecalculating, mutateAsync: updateBooking, variables } = useUpdateBooking();
const { confirmAndSave, isBusy } = usePriceChangeConfirmation();
const isSaving = computed(() => isRecalculating.value || isBusy.value);

const extraChanges = ref<ChangedBookedExtraTypes>({});
const newPricing = ref<Pricing | null>(null);
const isCalculatingPrice = computed(() => variables.value?.booking.dryRun && isRecalculating.value);
const hasPriceChanged = computed(
  () =>
    newPricing.value && newPricing.value.totalPrice !== props.booking.priceCalculation.totalPrice,
);

const isCustomBookedExtra = computed(() =>
  getCustomBookedExtras(props.booking.priceCalculation)?.some(
    (customBookedExtra) =>
      customBookedExtra.customVehicleExtraType === VehicleExtraType.ADDITIONAL_DRIVER,
  ),
);

const isDocumentScanningEnabled = useFeatureFlag('documents-scanning');

const normalizeAdditionalDrivers = (drivers: AdditionalDriver[]): AdditionalDriver[] =>
  drivers.map((driver) => ({
    ...driver,
    dateOfBirth: (driver.dateOfBirth
      ? fromCalendarDate(driver.dateOfBirth)
      : null) as AdditionalDriver['dateOfBirth'],
  }));

const {
  handleSubmit,
  values: formValues,
  validate,
  setFieldValue,
} = useForm({
  initialValues: {
    additionalDrivers: cloneDeep(normalizeAdditionalDrivers(props.booking.additionalDrivers)),
  },
  validationSchema: props.validationIsMandatory
    ? additionalDriversMandatorySchema(
        computed(() => props.booking.bookedCar.vehicleType.minAge),
        computed(() => props.booking.startDate),
      )
    : additionalDriversSchema,
});

watch(
  () => formValues.additionalDrivers,
  () => {
    if (!formValues.additionalDrivers) {
      setFieldValue('additionalDrivers', []);
    }
  },
);

const {
  remove: removeFromFieldArray,
  push: pushToFieldArray,
  replace: replaceFieldArray,
  fields,
} = useFieldArray<AdditionalDriver>('additionalDrivers');

watch(
  () => props.booking.additionalDrivers,
  () => {
    replaceFieldArray(cloneDeep(normalizeAdditionalDrivers(props.booking.additionalDrivers)));
    unchangedValues.value = cloneDeep(formValues);
  },
);

const push = (additionalDriver: AdditionalDriver) => {
  const extraOption = getExtraOption(fields.value.length + 1);
  if (!extraOption.chosenOption) return;
  pushToFieldArray(additionalDriver);
  calculatePriceForExtraChange(extraOption);
  if (isDocumentScanningEnabled.value && props.showLicenseScan) {
    emit('scan-documents', fields.value.length);
  }
};

const remove = (i: number) => {
  const extraOption = getExtraOption(fields.value.length - 1);
  if (!extraOption.chosenOption) return;
  removeFromFieldArray(i);
  calculatePriceForExtraChange(extraOption);
};

const calculatePriceForExtraChange = async (extraOption: {
  extra: VehicleExtra | undefined;
  chosenOption: VehicleExtraOption | undefined;
}) => {
  if (!extraOption.extra || !extraOption.chosenOption) return;

  const bookedExtra = getBookedExtras(props.booking.priceCalculation)?.find(
    (bookedExtra) => bookedExtra.vehicleExtraSnapshot.type === extraOption.extra!.type,
  );

  if (bookedExtra && bookedExtra.vehicleExtraOptionSnapshot.id !== extraOption.chosenOption.id) {
    extraChanges.value[extraOption.extra.type] = {
      vehicleExtraId: extraOption.extra.id,
      vehicleExtraOptionId: extraOption.chosenOption.id,
    };
  } else {
    extraChanges.value = {};
  }

  // Calculate new price with dryRun
  if (Object.keys(extraChanges.value).length > 0) {
    const result = await updateBooking({
      id: props.booking.id,
      booking: {
        pricing: {
          bookedExtras: Object.values(extraChanges.value).filter(Boolean) as BookedExtraDto[],
          priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
        },
        dryRun: true,
      },
      ...(props.handoverAdjustment ? { query: { handoverAdjustment: true as const } } : {}),
    });
    newPricing.value = result?.priceCalculation ?? null;
  } else {
    newPricing.value = null;
  }
};

const getExtraOption = (additionalDriversAmount: number) => {
  const extra = props.booking.priceCalculation?.availableExtras?.find(
    (extra) => extra.type === VehicleExtraType.ADDITIONAL_DRIVER,
  );
  return {
    extra: extra,
    chosenOption: extra?.options.find((option) => Number(option.value) === additionalDriversAmount),
  };
};

const unchangedValues = ref(cloneDeep(formValues));

const onSubmit = handleSubmit(async (values) => {
  const hasExtraChanges = Object.keys(extraChanges.value).length > 0;

  if (hasExtraChanges && newPricing.value) {
    const { status } = await confirmAndSave({
      booking: props.booking,
      priceDifferenceCounterparty: props.priceDifferenceCounterparty,
      handoverAdjustment: props.handoverAdjustment,
      confirmOnlyWhenCancellationFee: true,
      updateBookingDto: {
        ...values,
        pricing: {
          bookedExtras: Object.values(extraChanges.value).filter(Boolean) as BookedExtraDto[],
          priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
        },
      },
    });
    if (status !== 'saved') return;
  } else {
    await updateBooking({
      id: props.booking.id,
      booking: values,
    });
  }
  unchangedValues.value = cloneDeep(formValues);
  extraChanges.value = {};
  newPricing.value = null;
});

const hasFormChanged = useFormHasChanged(unchangedValues, formValues);
const hasChanged = computed(() => {
  return hasFormChanged?.value || Object.values(extraChanges.value).length > 0;
});
watch(hasChanged, () => emit('hasChanged', hasChanged.value));

const onCancel = () => {
  replaceFieldArray(cloneDeep(normalizeAdditionalDrivers(props.booking.additionalDrivers)));
  newPricing.value = null;
  extraChanges.value = {};
};

defineExpose({
  validate,
  setFieldValue,
});
</script>

<i18n lang="json">
{
  "en": {
    "firstName": "First Name",
    "lastName": "Last Name",
    "dateOfBirth": "Date Of Birth",
    "additionalDrivers": "Additional Drivers",
    "add": "+ Add Additional Driver",
    "deleteAllAdditionalDrivers": "Save to delete all Additional Drivers",
    "scanLicense": "Scan License",
    "newPrice": "New Price",
    "recalculatingPrice": "Recalculating Price..."
  },
  "de": {
    "firstName": "Vorname",
    "lastName": "Nachname",
    "dateOfBirth": "Geburtsdatum",
    "driversLicense": "Führerschein",
    "additionalDrivers": "Zusatzfahrer",
    "add": "+ Zusatzfahrer hinzufügen",
    "deleteAllAdditionalDrivers": "Speichern um alle Zusatzfahrer zu löschen",
    "scanLicense": "Führerschein scannen",
    "newPrice": "Neuer Preis",
    "recalculatingPrice": "Preis wird berechnet..."
  },
  "es": {
    "firstName": "Nombre",
    "lastName": "Apellidos",
    "dateOfBirth": "Fecha de nacimiento",
    "additionalDrivers": "Conductores adicionales",
    "add": "+ Añadir conductor adicional",
    "deleteAllAdditionalDrivers": "Guarde para eliminar todos los conductores adicionales",
    "scanLicense": "Escanear permiso de conducir",
    "newPrice": "Nuevo precio",
    "recalculatingPrice": "Recalculando el precio…"
  }
}
</i18n>
